
export type UserListSortKey = "id" | "name" | "company" | "totalTimeSpent" | "userAgent"

export interface ChartData {
	datasets: {
		label: string
		data: number[]
		backgroundColor: string | string[]
		borderColor: string | string[]
		borderWidth: number
	}[]
	labels: string[]
}

export interface User {
	/** From DB: User.companyId → Company.name */
	companyName: string
	email: string
	id: number
	name: string
	userAgent?: string
}

export interface UserAction {
	count: number
	lastTimestamp: Date
	totalTimeSpent?: number
}

export function aggregateBrowserStats(
	users: User[],
	userActions: Record<number, Record<string, UserAction>>
): { browser: string; userCount: number; totalHours: number }[] {
	const stats = new Map<string, { userCount: number; totalHours: number }>()

	for (const user of users) {
		if (user.userAgent == null || user.userAgent === "") continue
		const browser = getBrowser(user.userAgent)
		const hours = totalTimeSpentHours(userActions, user.id)
		const existing = stats.get(browser) ?? { userCount: 0, totalHours: 0 }
		stats.set(browser, {
			userCount: existing.userCount + 1,
			totalHours: existing.totalHours + hours,
		})
	}

	return [...stats.entries()]
		.map(([browser, s]) => ({ browser, ...s }))
		.sort((a, b) => b.userCount - a.userCount)
}

export function formatTimeSpent(hours: number): string {
	let remaining = Math.floor(hours * 3600)
	const units: { label: string; seconds: number }[] =
		remaining >= 86400
			? [
					{ label: "week", seconds: 604800 },
					{ label: "day", seconds: 86400 },
					{ label: "hour", seconds: 3600 },
					{ label: "minute", seconds: 60 },
				]
			: [
					{ label: "hour", seconds: 3600 },
					{ label: "minute", seconds: 60 },
					{ label: "second", seconds: 1 },
				]

	const parts: string[] = []
	for (const { label, seconds } of units) {
		if (remaining < seconds) continue
		const count = Math.floor(remaining / seconds)
		remaining %= seconds
		parts.push(`${count} ${label}${count !== 1 ? "s" : ""}`)
	}

	return parts.length > 0 ? parts.join(" ") : "0 seconds"
}

// CSV Generation Function
export function generateCSV(
	users: User[],
	userActions: Record<number, Record<string, UserAction>>
): string {
	const actionSet = new Set<string>()
	for (const user of users) {
		const userActionData = userActions[user.id]
		if (userActionData != null) {
			for (const actionKey of Object.keys(userActionData)) {
				const actionName = moduleNameFromActionKey(actionKey)
				if (actionName != null) actionSet.add(actionName)
			}
		}
	}

	const actionTypes = Array.from(actionSet).sort()

	const headers = [
		"User ID",
		"User Name",
		"Email",
		"Company",
		"User Agent",
		"Total Actions",
		"Total Time Spent",
		...actionTypes.flatMap((actionType) => [`${actionType} Count`, `${actionType} Duration`]),
	]

	const rows = users.map((user) => {
		const userActionData = userActions[user.id] ?? {}
		let totalActions = 0
		let totalTimeSpent = 0

		const actionCounts: Record<string, number> = {}
		const actionDurations: Record<string, number> = {}
		for (const actionType of actionTypes) {
			actionCounts[actionType] = 0
			actionDurations[actionType] = 0
		}

		for (const [actionKey, actionData] of Object.entries(userActionData)) {
			const actionName = moduleNameFromActionKey(actionKey)
			if (actionName != null && actionTypes.includes(actionName)) {
				const count = actionData.count ?? 0
				actionCounts[actionName] = (actionCounts[actionName] ?? 0) + count
				totalActions += count

				const duration = actionData.totalTimeSpent ?? 0
				actionDurations[actionName] = (actionDurations[actionName] ?? 0) + duration
				totalTimeSpent += duration
			}
		}

		const formattedTotalTimeSpent = formatTimeSpent(totalTimeSpent)
		const formattedActionDurations = actionTypes.map((actionType) =>
			formatTimeSpent(actionDurations[actionType] ?? 0)
		)

		return [
			user.id,
			user.name,
			user.email,
			user.companyName,
			user.userAgent ?? "",
			totalActions,
			formattedTotalTimeSpent,
			...actionTypes.flatMap((actionType, index) => [
				actionCounts[actionType],
				formattedActionDurations[index],
			]),
		]
	})

	return [headers, ...rows]
		.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
		.join("\n")
}

export function getBrowser(ua: string): string {
	ua = ua.toLowerCase()

	if (ua.includes("firefox") || ua.includes("fxios")) return "firefox"

	if (ua.includes("edg/") || ua.includes("edgios")) return "edge"

	if (ua.includes("opr/") || ua.includes("opera")) return "opera"

	if (
		(ua.includes("chrome") || ua.includes("crios")) &&
		!ua.includes("edg/") &&
		!ua.includes("opr/")
	) {
		return "chrome"
	}

	if (
		ua.includes("safari") &&
		!ua.includes("chrome") &&
		!ua.includes("crios") &&
		!ua.includes("android")
	) {
		return "safari"
	}

	return "other"
}

export function getSortIndicator(
	key: string,
	sortConfig: { key: string; direction: "asc" | "desc" } | null
): string | null {
	if (sortConfig?.key !== key) return null
	return sortConfig.direction === "asc" ? " ▲" : " ▼"
}

const IGNORED_USER_TRACKING_MODULES = new Set(["login", "clients", "map"])

export function moduleNameFromActionKey(actionKey: string): string | null {
	const module = actionKey.split("_")[0]?.trim()
	if (module == null) return null
	const lower = module.toLowerCase()
	return lower === "" ||
		lower === "null" ||
		lower === "undefined" ||
		IGNORED_USER_TRACKING_MODULES.has(lower)
		? null
		: module
}

// Chart Preparation Functions
export function prepareChartData(sortedUserActions: [string, UserAction][]): ChartData {
	const sortedByTimeSpent = [...sortedUserActions].sort(
		([, a], [, b]) => (b.totalTimeSpent ?? 0) - (a.totalTimeSpent ?? 0)
	)

	return {
		labels: sortedByTimeSpent.map(([action]) => action.split("_")[0] ?? action),
		datasets: [
			{
				label: "Time Spent (minutes)",
				data: sortedByTimeSpent.map(([, action]) => (action.totalTimeSpent ?? 0) * 60),
				backgroundColor: "rgba(21, 237, 158, 0.6)",
				borderColor: "rgba(75, 192, 192, 1)",
				borderWidth: 1,
			},
		],
	}
}

export function prepareVisitsForUserChartData(
	sortedUserActions: [string, UserAction][]
): ChartData {
	const sortedVisits = sortedUserActions
		.map(([action, data]) => ({
			action: action.split("_")[0] ?? action,
			count: data.count ?? 0,
		}))
		.sort((a, b) => b.count - a.count)

	return {
		labels: sortedVisits.map((e) => e.action),
		datasets: [
			{
				label: "Number of Visits",
				data: sortedVisits.map((e) => e.count),
				backgroundColor: "rgba(153, 102, 255, 0.6)",
				borderColor: "rgba(153, 102, 255, 1)",
				borderWidth: 1,
			},
		],
	}
}

export function prepareVisitsPerActionChartData(
	userActionsMap: Record<number, Record<string, UserAction>>
): ChartData {
	const actionCounts: Record<string, number> = {}

	for (const userActions of Object.values(userActionsMap)) {
		for (const [action, data] of Object.entries(userActions)) {
			const actionName = action.split("_")[0] ?? action
			actionCounts[actionName] = (actionCounts[actionName] ?? 0) + (data.count ?? 0)
		}
	}

	const sortedActions = Object.entries(actionCounts)
		.sort(([, a], [, b]) => b - a)
		.slice(0, TOP_N_ACTIONS)

	return {
		labels: sortedActions.map(([action]) => action),
		datasets: [
			{
				label: "Number of Visits",
				data: sortedActions.map(([, count]) => count),
				backgroundColor: "rgba(153, 102, 255, 0.6)",
				borderColor: "rgba(153, 102, 255, 1)",
				borderWidth: 1,
			},
		],
	}
}

export function prepareTimeSpentPerCompanyChartData(
	userActionsMap: Record<number, Record<string, UserAction>>,
	users: User[],
	selectedCompanies: string[],
	filterActive: boolean
): ChartData {
	const companyTotals: Record<string, number> = {}

	for (const user of users) {
		const { companyName } = user
		const companyMatch =
			selectedCompanies.length === 0 || selectedCompanies.includes(companyName)
		if (!companyMatch) continue

		const userActions = userActionsMap[user.id]
		if (filterActive && (userActions == null || Object.keys(userActions).length === 0)) continue

		let totalTime = 0
		for (const data of Object.values(userActions ?? {})) {
			totalTime += data.totalTimeSpent ?? 0
		}

		companyTotals[companyName] = (companyTotals[companyName] ?? 0) + totalTime
	}

	const sortedCompanies = Object.entries(companyTotals).sort(([, a], [, b]) => b - a)

	return {
		labels: sortedCompanies.map(([company]) => company),
		datasets: [
			{
				label: "Total Time Spent",
				data: sortedCompanies.map(([, time]) => time * 60),
				backgroundColor: "rgba(255, 159, 64, 0.6)",
				borderColor: "rgba(255, 159, 64, 1)",
				borderWidth: 1,
			},
		],
	}
}

export function prepareTotalTimeSpentChartData(
	userActionsMap: Record<number, Record<string, UserAction>>,
	selectedCompanies: string[],
	users: User[]
): ChartData {
	const userIdToCompany = new Map(users.map((u) => [u.id, u.companyName]))

	const filteredUserActionsMap = Object.fromEntries(
		Object.entries(userActionsMap).filter(([userIdStr]) => {
			const company = userIdToCompany.get(parseInt(userIdStr))
			return (
				selectedCompanies.length === 0 ||
				(company !== undefined && selectedCompanies.includes(company))
			)
		})
	)

	const actionTotals: Record<string, number> = {}
	for (const userActions of Object.values(filteredUserActionsMap)) {
		for (const [action, data] of Object.entries(userActions)) {
			const actionName = action.split("_")[0] ?? action
			if (data.totalTimeSpent != null) {
				actionTotals[actionName] = (actionTotals[actionName] ?? 0) + data.totalTimeSpent
			}
		}
	}

	const sortedActions = Object.entries(actionTotals)
		.sort(([, a], [, b]) => b - a)
		.slice(0, TOP_N_ACTIONS)

	return {
		labels: sortedActions.map(([action]) => action),
		datasets: [
			{
				label: "Total Time Spent (hours)",
				data: sortedActions.map(([, time]) => time),
				backgroundColor: "rgba(21, 237, 158, 0.6)",
				borderColor: "rgba(21, 237, 158, 1)",
				borderWidth: 1,
			},
		],
	}
}

export function prepareUsersPerCompanyChartData(filteredUsers: User[]): ChartData {
	const companyUserCounts: Record<string, number> = {}

	for (const user of filteredUsers) {
		if (user.companyName !== "") {
			companyUserCounts[user.companyName] = (companyUserCounts[user.companyName] ?? 0) + 1
		}
	}

	const sortedCompanies = Object.entries(companyUserCounts).sort(([, a], [, b]) => b - a)

	const colors = sortedCompanies.map((_, index) => {
		const hue = (index * 137.508) % 360 // golden angle approximation
		return `hsl(${hue}, 70%, 50%)`
	})

	return {
		labels: sortedCompanies.map(([company]) => company),
		datasets: [
			{
				label: "Number of Users",
				data: sortedCompanies.map(([, count]) => count),
				backgroundColor: colors,
				borderColor: "#fff",
				borderWidth: 1,
			},
		],
	}
}

export function sortTrackingUsers(
	users: User[],
	sort: { key: UserListSortKey; direction: "asc" | "desc" },
	userActions: Record<number, Record<string, UserAction>>
): User[] {
	const mul = sort.direction === "asc" ? 1 : -1
	return [...users].sort((a, b) => {
		switch (sort.key) {
			case "id":
				return (a.id - b.id) * mul
			case "name":
				return a.name.localeCompare(b.name) * mul
			case "company":
				return a.companyName.localeCompare(b.companyName) * mul
			case "userAgent":
				return (a.userAgent ?? "").localeCompare(b.userAgent ?? "") * mul
			case "totalTimeSpent":
			default: {
				const ta = totalTimeSpentHours(userActions, a.id)
				const tb = totalTimeSpentHours(userActions, b.id)
				return (ta - tb) * mul
			}
		}
	})
}

// Helper Functions
export function timeAgo(date: Date): string {
	const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
	const intervals = [
		{ label: "year", seconds: 31536000 },
		{ label: "month", seconds: 2592000 },
		{ label: "day", seconds: 86400 },
		{ label: "hour", seconds: 3600 },
		{ label: "minute", seconds: 60 },
		{ label: "second", seconds: 1 },
	] as const

	for (const interval of intervals) {
		const count = Math.floor(seconds / interval.seconds)
		if (count >= 1) {
			return `${count} ${interval.label}${count !== 1 ? "s" : ""} ago`
		}
	}
	return "Just now"
}

export function totalTimeSpentHours(
	userActions: Record<number, Record<string, UserAction>>,
	userId: number
): number {
	return Object.values(userActions[userId] ?? {}).reduce(
		(total, action) => total + (action.totalTimeSpent ?? 0),
		0
	)
}

export function totalVisitsForUser(
	userActions: Record<number, Record<string, UserAction>>,
	userId: number
): number {
	return Object.values(userActions[userId] ?? {}).reduce((sum, action) => sum + action.count, 0)
}

export function summarizeTopModules(userActions: Record<string, UserAction>, limit = 3): string {
	const byModule = new Map<string, { hours: number; visits: number }>()

	for (const [actionKey, action] of Object.entries(userActions)) {
		const module = moduleNameFromActionKey(actionKey)
		if (module == null) continue
		const current = byModule.get(module) ?? { hours: 0, visits: 0 }
		byModule.set(module, {
			hours: current.hours + (action.totalTimeSpent ?? 0),
			visits: current.visits + action.count,
		})
	}

	if (byModule.size === 0) return "—"

	return [...byModule.entries()]
		.sort((a, b) => {
			const hourDiff = b[1].hours - a[1].hours
			return hourDiff !== 0 ? hourDiff : b[1].visits - a[1].visits
		})
		.slice(0, limit)
		.map(([module, stats]) => {
			const detail =
				stats.hours > 0 ? formatTimeSpent(stats.hours) : `${stats.visits} visit${stats.visits !== 1 ? "s" : ""}`
			return `${module} (${detail})`
		})
		.join(", ")
}

export function userMatchesSearch(user: User, query: string): boolean {
	const t = query.trim().toLowerCase()
	if (t === "") return true
	return (
		String(user.id).includes(t) ||
		user.name.toLowerCase().includes(t) ||
		user.email.toLowerCase().includes(t) ||
		user.companyName.toLowerCase().includes(t) ||
		(user.userAgent ?? "").toLowerCase().includes(t)
	)
}

const TOP_N_ACTIONS = 10
